import { action, computed, makeObservable, observable } from 'mobx';

import {
  TeamRoleEnum,
  type DiscoverableTeam,
  type TeamDashboardData,
  type TeamPricing,
} from './team.types';

export class TeamStore {
  dashboard: TeamDashboardData | null = null;
  isLoading: boolean = true;
  error: string | null = null;

  checkoutLoading: boolean = false;
  portalLoading: boolean = false;
  inviteLoading: boolean = false;
  joinLoading: boolean = false;
  mutatingMemberId: string | null = null;

  discoverableTeam: DiscoverableTeam | null = null;
  mutatingDomain: string | null = null;
  joinByDomainLoading: boolean = false;
  pricingMap: Record<string, number> | null = null;

  constructor() {
    makeObservable(this, {
      dashboard: observable,
      isLoading: observable,
      error: observable,
      checkoutLoading: observable,
      portalLoading: observable,
      inviteLoading: observable,
      joinLoading: observable,
      mutatingMemberId: observable,
      discoverableTeam: observable,
      mutatingDomain: observable,
      joinByDomainLoading: observable,
      pricingMap: observable.ref,

      isAdmin: computed,
      isOwner: computed,

      setDashboard: action,
      setError: action,
      setIsLoading: action,
      setCheckoutLoading: action,
      setPortalLoading: action,
      setInviteLoading: action,
      setJoinLoading: action,
      setMutatingMemberId: action,
      setDiscoverableTeam: action,
      setMutatingDomain: action,
      setJoinByDomainLoading: action,
      setPricing: action,
    });
  }

  getPriceCents(tier: string, interval: string): number | null {
    return this.pricingMap?.[`${tier}_${interval}`] ?? null;
  }

  get isAdmin(): boolean {
    const role = this.dashboard?.team.role;
    return role === TeamRoleEnum.enum.owner || role === TeamRoleEnum.enum.admin;
  }

  get isOwner(): boolean {
    return this.dashboard?.team.role === TeamRoleEnum.enum.owner;
  }

  setDashboard(data: TeamDashboardData | null): void {
    this.dashboard = data;
  }

  setError(error: string | null): void {
    this.error = error;
  }

  setIsLoading(loading: boolean): void {
    this.isLoading = loading;
  }

  setCheckoutLoading(loading: boolean): void {
    this.checkoutLoading = loading;
  }

  setPortalLoading(loading: boolean): void {
    this.portalLoading = loading;
  }

  setInviteLoading(loading: boolean): void {
    this.inviteLoading = loading;
  }

  setJoinLoading(loading: boolean): void {
    this.joinLoading = loading;
  }

  setMutatingMemberId(id: string | null): void {
    this.mutatingMemberId = id;
  }

  setDiscoverableTeam(team: DiscoverableTeam | null): void {
    this.discoverableTeam = team;
  }

  setMutatingDomain(domain: string | null): void {
    this.mutatingDomain = domain;
  }

  setJoinByDomainLoading(loading: boolean): void {
    this.joinByDomainLoading = loading;
  }

  setPricing(tiers: TeamPricing['tiers'] | null): void {
    this.pricingMap = tiers
      ? Object.fromEntries(
          tiers.map(({ tier, interval, amount_cents }) => [
            `${tier}_${interval}`,
            amount_cents,
          ])
        )
      : null;
  }
}

export const teamStore = new TeamStore();
